import type {
  DirectiveNode,
  DefinitionNode,
  OperationDefinitionNode,
  FieldNode,
  DocumentNode,
} from 'graphql';
import {Kind} from 'graphql';
import {copyHiddenDocumentMetadata} from '@sail/data/dist/internal/shared/utils/hiddenDocumentMetadata';

type TransformationMetadata = {
  topLevelFieldAlias: string;
};

export function transformRestDirective(
  directive: DirectiveNode,
  metadata: TransformationMetadata,
): DirectiveNode {
  if (directive.name.value !== 'rest') {
    return directive;
  }
  // Find the queryName argument
  const queryNameArg = directive.arguments?.find(
    (arg) => arg.name.value === 'queryName',
  );

  if (!queryNameArg || queryNameArg.value.kind !== Kind.STRING) {
    return directive;
  }
  // Create a new queryName argument instead of overwriting
  const newQueryNameArg = {
    ...queryNameArg,
    value: {
      kind: Kind.STRING,
      value: metadata.topLevelFieldAlias,
    },
  };

  return {
    ...directive,
    arguments: directive.arguments?.map((arg) =>
      arg.name.value === 'queryName' ? newQueryNameArg : arg,
    ),
  };
}

export default function transformDocumentWithRest(
  document: DocumentNode,
): DocumentNode {
  const firstQuery = document.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === 'query',
  );

  if (!firstQuery) {
    throw new Error(
      'No query defined in the GraphQL document passed to transformDocumentWithRest.',
    );
  }

  const transformedQuery: OperationDefinitionNode = {
    ...firstQuery,
    selectionSet: {
      ...firstQuery.selectionSet,
      selections: firstQuery.selectionSet.selections.map((selection) => {
        if (selection.kind !== Kind.FIELD) {
          return selection;
        }
        return transformTopLevelField(selection);
      }),
    },
  };

  const transformedDefinitions: DefinitionNode[] = document.definitions.map(
    (def: DefinitionNode) => (def === firstQuery ? transformedQuery : def),
  );

  return copyHiddenDocumentMetadata(
    document,
    {
      ...document,
      definitions: transformedDefinitions,
    },
    {preserveTargetValues: true},
  );
}
function getFieldAlias(field: FieldNode): string {
  const {alias} = field;

  // TODO: this should be supported so independent queries work correctly
  if (!alias) {
    throw new Error(
      'No alias on top level field defined in the GraphQL document passed to transformDocumentWithRest.',
    );
  }
  return alias.value;
}

function transformTopLevelField(field: FieldNode): FieldNode {
  if (!field.directives) {
    return field;
  }

  const topLevelFieldAlias = getFieldAlias(field);

  const transformedDirectives = field.directives.map((directive) =>
    transformRestDirective(directive, {
      topLevelFieldAlias,
    }),
  );

  return {
    ...field,
    directives: transformedDirectives,
  };
}
